<!-- Features Section -->
<section
  id="features"
  class="py-24 bg-slate-50 border-t border-slate-200/60"
  aria-labelledby="features-heading"
>
  <div class="container mx-auto px-6">
    <!-- Section Header -->
    <div class="text-center max-w-3xl mx-auto mb-16 space-y-4">
      <div
        class="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider"
      >
        <i class="fa-solid fa-wand-magic-sparkles"></i>
        <span>Platform Features</span>
      </div>
      <h2
        id="features-heading"
        class="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight"
      >
        Why Students & Parents Choose Our Platform
      </h2>
      <p class="text-lg text-slate-600 leading-relaxed">
        Everything you need to excel in your studies, built specifically for Sri Lankan secondary and higher education streams.
      </p>
    </div>

    <!-- Main Features Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
      <!-- Feature 1: Expert Instructors -->
      <div
        class="daisy-card bg-white shadow-sm hover:shadow-md border border-slate-200/80 rounded-2xl transition-all duration-300 hover:-translate-y-1"
      >
        <div class="daisy-card-body p-8 space-y-4">
          <div
            class="w-14 h-14 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center text-2xl font-bold"
          >
            <i class="fa-solid fa-chalkboard-user"></i>
          </div>
          <h3 class="text-xl font-bold text-slate-900">
            Expert Sri Lankan Tutors
          </h3>
          <p class="text-slate-600 leading-relaxed text-sm">
            Learn directly from qualified teachers and island-ranked tutors with proven track records in Sri Lankan O/L and A/L examinations.
          </p>
          <div class="pt-2 text-xs font-semibold text-blue-600 flex items-center gap-1">
            <span>Verified teaching faculty</span>
            <i class="fa-solid fa-arrow-right"></i>
          </div>
        </div>
      </div>

      <!-- Feature 2: Live Interactive Classes -->
      <div
        class="daisy-card bg-white shadow-sm hover:shadow-md border border-slate-200/80 rounded-2xl transition-all duration-300 hover:-translate-y-1"
      >
        <div class="daisy-card-body p-8 space-y-4">
          <div
            class="w-14 h-14 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center text-2xl font-bold"
          >
            <i class="fa-solid fa-video"></i>
          </div>
          <h3 class="text-xl font-bold text-slate-900">
            Live Zoom Integration
          </h3>
          <p class="text-slate-600 leading-relaxed text-sm">
            Participate in real-time interactive lectures, clear doubts instantly with Q&A sessions, and download lesson notes seamlessly.
          </p>
          <div class="pt-2 text-xs font-semibold text-purple-600 flex items-center gap-1">
            <span>Automated meeting dispatch</span>
            <i class="fa-solid fa-arrow-right"></i>
          </div>
        </div>
      </div>

      <!-- Feature 3: Learn at Your Pace -->
      <div
        class="daisy-card bg-white shadow-sm hover:shadow-md border border-slate-200/80 rounded-2xl transition-all duration-300 hover:-translate-y-1"
      >
        <div class="daisy-card-body p-8 space-y-4">
          <div
            class="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center text-2xl font-bold"
          >
            <i class="fa-solid fa-clock-rotate-left"></i>
          </div>
          <h3 class="text-xl font-bold text-slate-900">
            Recorded Class Replays
          </h3>
          <p class="text-slate-600 leading-relaxed text-sm">
            Missed a class? Rewatch recorded video lessons anytime with full HD quality, speed controls, and downloadable reference resources.
          </p>
          <div class="pt-2 text-xs font-semibold text-emerald-600 flex items-center gap-1">
            <span>24/7 video archive</span>
            <i class="fa-solid fa-arrow-right"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Additional Benefits Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      <div class="daisy-card bg-white border border-slate-200/80 p-6 rounded-2xl text-center shadow-sm">
        <div class="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center mx-auto mb-3">
          <i class="fa-solid fa-certificate text-lg"></i>
        </div>
        <h4 class="font-bold text-slate-900 text-base mb-1">Quality Assured</h4>
        <p class="text-xs text-slate-500">Strictly aligned with national syllabi</p>
      </div>

      <div class="daisy-card bg-white border border-slate-200/80 p-6 rounded-2xl text-center shadow-sm">
        <div class="w-12 h-12 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center mx-auto mb-3">
          <i class="fa-solid fa-users text-lg"></i>
        </div>
        <h4 class="font-bold text-slate-900 text-base mb-1">Peer Community</h4>
        <p class="text-xs text-slate-500">Discuss paper solutions with peers</p>
      </div>

      <div class="daisy-card bg-white border border-slate-200/80 p-6 rounded-2xl text-center shadow-sm">
        <div class="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-3">
          <i class="fa-solid fa-chart-line text-lg"></i>
        </div>
        <h4 class="font-bold text-slate-900 text-base mb-1">Progress Tracking</h4>
        <p class="text-xs text-slate-500">Monitor attendance and results</p>
      </div>

      <div class="daisy-card bg-white border border-slate-200/80 p-6 rounded-2xl text-center shadow-sm">
        <div class="w-12 h-12 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center mx-auto mb-3">
          <i class="fa-solid fa-headset text-lg"></i>
        </div>
        <h4 class="font-bold text-slate-900 text-base mb-1">Dedicated Support</h4>
        <p class="text-xs text-slate-500">Fast assistance whenever needed</p>
      </div>
    </div>

  </div>
</section>
